/**
 * Forward cash balance projection for the Finances forecast chart.
 *
 * projected balance(month) = prior balance
 *   + AR installments due that month (unpaid billing-schedule rows)
 *   − supplier bills due that month (open AP)
 *   − issued card POs not yet marked purchased (no supplier bill)
 *     Overhead POs inside a set OpEx budget sit in Burn instead of AP.
 *     That month's burn rises only by the amount those POs exceed the envelope.
 *   − monthly burn (overhead + payroll forecast buckets)
 *   − planned obligations (bonuses, TN S-Corp tax, other dated cash-outs)
 *   − leftover job costs (design / install / freight / travel residuals)
 *
 * Timing policy (conservative):
 *  - Unpaid AR stays on the line until it is marked paid (or dismissed) on
 *    Billing & Payables — including invoiced rows whose invoice/due date is
 *    already past. Overdue or undated AR lands in the starting month.
 *  - Overdue or undated AP bills land in the starting month — cash owed now.
 *  - Leftover job costs dated before the starting month roll into the current
 *    month while related tasks/groups are still open; finished/closed work drops.
 *  - Unapproved timber/CNC proposal quotes follow the midpoint of the
 *    matching task / structure subtask. They are not stacked on Today.
 *  - AR beyond the projection window is excluded from the balance math but
 *    reported in `arBeyondWindow` for context.
 *
 * Pure module — no firebase imports (unit-testable).
 */

import type { PurchaseSpendClass } from '../types/purchasing';
import {
  opexBudgetBurnExcessByMonth,
  opexBudgetGroupForIssuedPurchase,
  type IssuedPurchaseForecastSlice,
} from './issuedPurchaseForecast';
import type { ContractorInvoiceForecastSlice } from './projectJobCostForecast';
import { isReceivableSettled, type ReadyToBillRow } from './readyToBill';
import {
  collapseDuplicateCncAwardBills,
  describeSupplierInstallmentTrigger,
  expandSupplierBillPayments,
  supplierBillPaidAt,
  type SupplierBill,
} from './supplierBills';
import type { ForecastBucket } from './financesForecast';
import { toMonthKey } from './financesForecast';
import type { ObligationSlice, PlannedKind } from '../types/finances';
import type { PayrollSchedule } from './leaveAccrual';
import {
  listPayDatesInRange,
  parsePayrollSchedule,
  scaleMonthlyPayrollForPaydays,
} from './leaveAccrual';
import {
  DEFAULT_BILL_PAY_CLEARING,
  inferBillPayMethod,
  isBillInClearing,
  resolveBillClearingDate,
  type BillPayClearingSettings,
} from './billPayClearing';

export type JobCostBucket = 'design' | 'install' | 'freight' | 'travel' | 'materials';

/** Leftover proposal job cost dated onto the cash line. */
export interface JobCostProjectionSlice {
  dealId: string;
  projectId?: string;
  projectLabel: string;
  projectName?: string;
  bucket: JobCostBucket;
  freightLeg?: 'inbound' | 'outbound';
  amount: number;
  dueDate: Date | null;
  estimated: boolean;
  anchor?: string;
  taskId?: string;
}

function jobCostItemLabel(row: JobCostProjectionSlice): string {
  if (row.bucket === 'freight' && row.freightLeg === 'inbound') return 'Freight inbound';
  if (row.bucket === 'freight' && row.freightLeg === 'outbound') return 'Freight outbound';
  if (row.bucket === 'design') return 'Design';
  if (row.bucket === 'install') return 'Install';
  if (row.bucket === 'materials') return 'Materials';
  return 'Travel';
}

/**
 * One contributing invoice installment (AR) or bill installment (AP) inside a
 * projection month — powers the click-to-inspect breakdown on the forecast.
 */
export interface CashFlowProjectionItem {
  kind: 'ar' | 'ap' | 'planned' | 'job';
  plannedKind?: PlannedKind;
  /** Design / install / freight / travel leftover — only when kind is `job`. */
  jobBucket?: JobCostBucket;
  /** Project (AR) or vendor (AP) the money belongs to. */
  projectLabel: string;
  /** What the payment is — "50% installment", "Payment 2 of 2 — balance". */
  label: string;
  amount: number;
  /**
   * Real due date. Overdue/undated items land in the current month but keep
   * this original date (or null) so the breakdown can show why.
   */
  dueDate: Date | null;
  estimated: boolean;
  /** How the due date was derived — e.g. "net 30 after Timber Lead Time — task ends Oct 7, 2026". */
  anchor?: string;
  /** Timber / CRM project name — shown as a badge on the month breakdown. */
  projectName?: string;
  /** Timber project id — used with `taskId` to open the timeline. */
  projectId?: string;
  /** CRM deal this AP/AR row belongs to, when it is job spend rather than company OpEx. */
  dealId?: string;
  /** Job / overhead / CapEx — issued POs carry this so overhead stays on the PO line. */
  spendClass?: PurchaseSpendClass;
  /**
   * Open approved overhead PO that is covered by an OpEx budget. Shown under
   * Burn and omitted from AP so it is not stacked on top of the envelope.
   */
  withinBurnBudget?: boolean;
  /** OpEx group id when `withinBurnBudget` is set. */
  overheadGroup?: string;
  /** Live schedule task this row is anchored to. */
  taskId?: string;
  /** Supplier bill this AP item came from — powers hover + Quick Look on the forecast. */
  billId?: string;
  /** Issued purchase this row came from. Opens that PO. */
  requisitionId?: string;
  /** Billing & Payables ready-item id for an AR installment. */
  billingItemId?: string;
  /** Unpaid contractor invoice. */
  contractorInvoiceId?: string;
  /** Approved quote that has no supplier bill yet. */
  submissionId?: string;
  /** Quote, invoice, or receipt stored on an issued PO. */
  previewStoragePath?: string;
  previewFileName?: string;
  previewFileUrl?: string;
  /** True when this row is a CFO sandbox overlay, not a live bill/invoice. */
  scenario?: boolean;
  /**
   * Original forecast key when this row was moved to today by Pay now.
   * The breakdown uses it to turn the simulation off.
   */
  payNowKey?: string;
  /** Unawarded timber/CNC spend taken from proposal nodes — not a supplier bill. */
  quotePlaceholder?: boolean;
  /** Timber or CNC track for an unawarded quote placeholder. */
  quoteKind?: 'timber' | 'cnc';
  /** Proposal structure(s) this spend is for — shown as badges on the breakdown. */
  structureNames?: string[];
}

export interface CashFlowProjectionMonth {
  monthKey: string;
  /** First day of the month, local time. */
  month: Date;
  /** Expected client payments (unpaid AR due this month). */
  arIn: number;
  /** Supplier bills due this month (open AP). */
  apOut: number;
  /** Overhead + payroll forecast for this month. */
  burn: number;
  /** Loaded payroll portion of `burn` (daily walk lumps this on pay dates). */
  payrollBurn: number;
  /** Bonuses, tax, and other dated planned cash-outs. */
  plannedOut: number;
  /** Leftover design / install / freight / travel job costs. */
  jobOut: number;
  /** Net movement: arIn − apOut − burn − plannedOut − jobOut. */
  net: number;
  /** Projected balance at end of this month. */
  endingBalance: number;
  /** True when any AR contribution this month has an estimated due date. */
  hasEstimatedAr: boolean;
  /** Every invoice/bill installment contributing to this month's arIn/apOut. */
  items: CashFlowProjectionItem[];
}

export interface CashFlowProjection {
  months: CashFlowProjectionMonth[];
  startingBalance: number;
  /** Lowest projected end-of-month balance in the window (null when no months). */
  lowestBalance: number | null;
  /** Month label/key where the lowest balance occurs. */
  lowestBalanceMonthKey: string | null;
  /**
   * Months until the projected balance first drops below zero.
   * `null` means the balance stays positive for the whole window.
   */
  runwayMonths: number | null;
  /** Total unpaid AR due after the projection window ends. */
  arBeyondWindow: number;
  /** Totals across the window, for KPI display. */
  totals: { arIn: number; apOut: number; burn: number; plannedOut: number; jobOut: number };
}

export interface CashFlowProjectionInputs {
  /** Latest known cash balance (e.g. Sequence pod total). */
  startingBalance: number;
  /**
   * Unpaid money-in rows from `buildReadyToBillQueue({ forProjectSummary: true })`.
   * Filtering to unpaid rows happens here — pass the full queue.
   */
  moneyInRows: ReadyToBillRow[];
  /** All supplier bills — open (not paid/void) bills are selected here. */
  supplierBills: SupplierBill[];
  /**
   * Task end dates by task id — resolves task-anchored supplier payment terms
   * (e.g. "50% when Timber lead time completes") to live schedule dates.
   */
  supplierTaskEndDates?: Map<string, Date>;
  /** Task names by id — labels task anchors when the term stored no title. */
  taskNameById?: Map<string, string>;
  /** Timber project display name by project id. */
  projectNameById?: Map<string, string>;
  /** Preferred timber project id for a deal (live schedule wins when several exist). */
  projectIdByDealId?: Map<string, string>;
  /** Task → timber project id, so a bill with only a task still deep-links. */
  taskProjectIdById?: Map<string, string>;
  /** Monthly burn buckets from `forecastMonthly()`, starting at the current month. */
  burnBuckets: ForecastBucket[];
  /** Dated bonus / tax / other planned cash-outs (future months only). */
  plannedItems?: ObligationSlice[];
  /** Leftover proposal job costs (design / install / freight / travel). */
  jobCostItems?: JobCostProjectionSlice[];
  /**
   * Unawarded timber/CNC spend from proposal nodes — treated as estimated AP
   * until those structures are awarded. Leftovers may sit next to official
   * bills for structures that already have a quote.
   */
  quotePlaceholderAp?: Array<{
    monthKey: string;
    amount: number;
    dealId: string;
    projectId?: string;
    projectLabel: string;
    kind: 'timber' | 'cnc';
    taskId?: string;
    dueDate: Date | null;
    structureName?: string;
  }>;
  /**
   * Issued POs (usually card) that have no supplier bill yet. Dropped once
   * marked purchased or when an ACH bill already covers the requisition.
   */
  issuedPurchaseAp?: IssuedPurchaseForecastSlice[];
  /**
   * Approved timber/CNC structure quotes that have no supplier bill of their
   * own. Lands in the current month until a bill (and its terms) exist.
   */
  /** Open award bills replaced by `awardedQuoteAp` because the amount is stale. */
  omitSupplierBillIds?: string[];
  awardedQuoteAp?: Array<{
    amount: number;
    vendorName: string;
    label: string;
    dealId: string;
    projectId?: string;
    structureNames?: string[];
    submissionId: string;
  }>;
  /**
   * Unpaid contractor install invoices. The install leftover is already
   * reduced by these dollars, so they are the dated cash line.
   */
  contractorInvoiceAp?: ContractorInvoiceForecastSlice[];
  /**
   * Monthly equivalent of each OpEx group that has a budget set.
   * Approved open POs in those groups fold into Burn for their month.
   */
  opexBudgetMonthlyByGroup?: Record<string, number>;
  /**
   * When set, monthly payroll burn is scaled by payday count so a 3-payday
   * month charges three full checks (not average-month payroll÷3 on the chart).
   */
  payrollSchedule?: PayrollSchedule | null;
  /**
   * AP bank-clearing lag after Mark paid. Paid bills stay on the cash line
   * until QB clears (or method lag elapses). Shared with financeAgentApi.
   */
  billPayClearing?: BillPayClearingSettings | null;
  /** Number of months to project, starting with the current month. */
  monthsAhead: number;
  now?: Date;
}

/** Unpaid AR rows that still represent expected cash in. */
export function isOpenReceivable(row: ReadyToBillRow): boolean {
  if (isReceivableSettled(row)) return false;
  return row.amount > 0;
}

/**
 * AR that should move the projected cash line.
 * Follows Billing & Payables: unpaid (including invoiced / sent) stays until
 * marked paid or dismissed. A passed invoice or due date does not drop it.
 */
export function isProjectedReceivable(row: ReadyToBillRow, _now: Date = new Date()): boolean {
  return isOpenReceivable(row);
}

/** Open AP bills that still represent expected cash out until paid or deleted (void). */
export function isOpenSupplierBill(bill: SupplierBill): boolean {
  if (bill.status === 'paid' || bill.status === 'void') return false;
  return (Number(bill.amount) || 0) > 0;
}

/**
 * Forecast AP: unpaid open bills, plus paid bills still in bank clearing.
 * Billing & Payables "open" counts keep using {@link isOpenSupplierBill}.
 */
export function isForecastSupplierBill(
  bill: SupplierBill,
  now: Date = new Date(),
  settings: BillPayClearingSettings = DEFAULT_BILL_PAY_CLEARING,
): boolean {
  if (bill.status === 'void') return false;
  if ((Number(bill.amount) || 0) <= 0) return false;
  if (isOpenSupplierBill(bill)) return true;
  if (bill.status !== 'paid') return false;
  return isBillInClearing(
    {
      status: bill.status,
      paidAt: supplierBillPaidAt(bill),
      paymentMethod: bill.paymentMethod,
      qbClearedAt: bill.qbClearedAt,
      qbPaymentDepositDate: bill.qbPaymentDepositDate,
    },
    now,
    settings,
  );
}

function fmtAnchorDate(d: Date): string {
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function startOfMonth(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}

function addMonths(d: Date, n: number): Date {
  return new Date(d.getFullYear(), d.getMonth() + n, 1);
}

export function buildCashFlowProjection(
  inputs: CashFlowProjectionInputs,
): CashFlowProjection {
  const now = inputs.now ?? new Date();
  const monthsAhead = Math.max(0, Math.floor(inputs.monthsAhead));
  const firstMonth = startOfMonth(now);
  const firstMonthKey = toMonthKey(firstMonth);
  const lastMonthKey = monthsAhead > 0 ? toMonthKey(addMonths(firstMonth, monthsAhead - 1)) : firstMonthKey;

  const arByMonth = new Map<string, number>();
  const arEstimatedMonths = new Set<string>();
  const itemsByMonth = new Map<string, CashFlowProjectionItem[]>();
  let arBeyondWindow = 0;

  const pushItem = (key: string, item: CashFlowProjectionItem) => {
    const list = itemsByMonth.get(key);
    if (list) list.push(item);
    else itemsByMonth.set(key, [item]);
  };

  const resolveProject = (
    opts: { projectId?: string; dealId?: string; taskId?: string },
  ): { projectId?: string; projectName?: string } => {
    const fromTask = opts.taskId ? inputs.taskProjectIdById?.get(opts.taskId) : undefined;
    const fromDeal = opts.dealId ? inputs.projectIdByDealId?.get(opts.dealId) : undefined;
    const projectId = opts.projectId || fromDeal || fromTask;
    const projectName = projectId ? inputs.projectNameById?.get(projectId) : undefined;
    return { projectId, projectName };
  };

  for (const row of inputs.moneyInRows) {
    if (!isProjectedReceivable(row, now)) continue;
    const due = row.dueDate;
    let key: string;
    if (!due) {
      // Undated: billable-now rows are expected imminently; others are
      // unknown-timing, so keep them conservative in the current month too —
      // excluding them would overstate risk of the whole window instead.
      key = firstMonthKey;
    } else {
      const dueKey = toMonthKey(startOfMonth(due));
      key = dueKey < firstMonthKey ? firstMonthKey : dueKey;
    }
    if (key > lastMonthKey) {
      arBeyondWindow += row.amount;
      continue;
    }
    arByMonth.set(key, (arByMonth.get(key) ?? 0) + row.amount);
    if (row.estimated || !due) arEstimatedMonths.add(key);
    const arProject = resolveProject({
      projectId: row.projectId,
      dealId: row.dealId,
      taskId: row.anchorTaskId,
    });
    pushItem(key, {
      kind: 'ar',
      projectLabel: row.dealLabel,
      label: row.kind === 'change_order'
        ? `Change order ${row.label}`
        : `${row.label} installment`,
      amount: Math.round(row.amount),
      dueDate: due ?? null,
      estimated: row.estimated || !due,
      anchor: row.dueAnchor || row.detail || undefined,
      projectName: arProject.projectName || row.dealLabel,
      projectId: arProject.projectId,
      dealId: row.dealId,
      taskId: row.anchorTaskId,
      billingItemId: row.tracked?.id,
    });
  }

  const apByMonth = new Map<string, number>();
  const omitBillIds = new Set(inputs.omitSupplierBillIds ?? []);
  const clearingSettings = inputs.billPayClearing ?? DEFAULT_BILL_PAY_CLEARING;
  for (const bill of collapseDuplicateCncAwardBills(inputs.supplierBills)) {
    if (bill.id && omitBillIds.has(bill.id)) continue;
    if (!isForecastSupplierBill(bill, now, clearingSettings)) continue;

    // Paid but still bank-clearing — keep the cash-out on the clearing date.
    if (bill.status === 'paid') {
      const paidAt = supplierBillPaidAt(bill);
      const clearingInput = {
        status: bill.status,
        paidAt,
        paymentMethod: bill.paymentMethod,
        qbClearedAt: bill.qbClearedAt,
        qbPaymentDepositDate: bill.qbPaymentDepositDate,
      };
      const clearDate = resolveBillClearingDate(clearingInput, clearingSettings);
      const amount = Math.round(Number(bill.amount) || 0);
      if (amount <= 0) continue;
      const due = clearDate;
      let key: string;
      if (!due) {
        key = firstMonthKey;
      } else {
        const dueKey = toMonthKey(startOfMonth(due));
        key = dueKey < firstMonthKey ? firstMonthKey : dueKey;
      }
      if (key > lastMonthKey) key = lastMonthKey;
      apByMonth.set(key, (apByMonth.get(key) ?? 0) + amount);
      const method = inferBillPayMethod(bill.paymentMethod);
      const apProject = resolveProject({
        projectId: bill.projectId,
        dealId: bill.dealId,
      });
      pushItem(key, {
        kind: 'ap',
        projectLabel: bill.vendorName,
        label: `Clearing (${method.toUpperCase()})`,
        amount,
        dueDate: due ?? null,
        estimated: false,
        anchor: due ? `bank clear ${fmtAnchorDate(due)}` : 'bank clearing',
        projectName: apProject.projectName,
        projectId: apProject.projectId,
        dealId: bill.dealId,
        billId: bill.id,
      });
      continue;
    }

    // Bills with payment terms split into installments (down payments, task-
    // anchored balances, net terms); others fall back to one payment at dueDate.
    for (const slice of expandSupplierBillPayments(bill, {
      taskEndDateById: inputs.supplierTaskEndDates,
    })) {
      // Installments already marked paid are no longer future cash out.
      if (
        slice.installmentIndex !== undefined &&
        bill.installmentPayments?.[slice.installmentIndex]
      ) {
        continue;
      }
      const amount = Math.round(slice.amount);
      if (amount <= 0) continue;
      const due = slice.dueDate;
      let key: string;
      if (!due) {
        key = firstMonthKey;
      } else {
        const dueKey = toMonthKey(startOfMonth(due));
        key = dueKey < firstMonthKey ? firstMonthKey : dueKey;
      }
      // Bills due beyond the window still land in the last month — money owed
      // is a commitment, unlike AR which we don't count on early.
      if (key > lastMonthKey) key = lastMonthKey;
      apByMonth.set(key, (apByMonth.get(key) ?? 0) + amount);

      const inst =
        slice.installmentIndex !== undefined
          ? bill.paymentTerms?.installments[slice.installmentIndex]
          : undefined;
      let anchor: string | undefined;
      if (inst) {
        const fallbackTitle =
          !inst.taskTitle && inst.taskId ? inputs.taskNameById?.get(inst.taskId) : undefined;
        anchor = describeSupplierInstallmentTrigger(
          fallbackTitle ? { ...inst, taskTitle: fallbackTitle } : inst,
        );
        if (inst.trigger === 'task_complete' && inst.taskId) {
          const taskEnd = inputs.supplierTaskEndDates?.get(inst.taskId);
          anchor += taskEnd
            ? ` — task ends ${fmtAnchorDate(taskEnd)}`
            : ' — task end date unknown';
        }
      } else if (bill.dueDate) {
        anchor = `bill due date ${fmtAnchorDate(bill.dueDate)}`;
      }
      const apProject = resolveProject({
        projectId: bill.projectId,
        dealId: bill.dealId,
        taskId: inst?.taskId,
      });
      pushItem(key, {
        kind: 'ap',
        projectLabel: slice.vendorName,
        label: slice.label,
        amount,
        dueDate: due ?? null,
        estimated: slice.estimated,
        anchor,
        projectName: apProject.projectName,
        projectId: apProject.projectId,
        dealId: bill.dealId,
        taskId: inst?.taskId,
        billId: bill.id,
      });
    }
  }

  for (const row of inputs.quotePlaceholderAp ?? []) {
    const amount = Math.round(row.amount);
    if (amount <= 0) continue;
    const due = row.dueDate;
    let key = due ? toMonthKey(startOfMonth(due)) : row.monthKey;
    if (!key) key = firstMonthKey;
    // Follow the task midpoint — do not stack unapproved quotes on this month.
    if (key < firstMonthKey) continue;
    if (key > lastMonthKey) key = lastMonthKey;
    apByMonth.set(key, (apByMonth.get(key) ?? 0) + amount);
    const track = row.kind === 'timber' ? 'Timber' : 'CNC';
    const structure = (row.structureName || '').trim();
    const apProject = resolveProject({
      projectId: row.projectId,
      dealId: row.dealId,
      taskId: row.taskId,
    });
    pushItem(key, {
      kind: 'ap',
      projectLabel: row.projectLabel,
      label: structure
        ? `${track} (proposal — not finalized) · ${structure}`
        : `${track} (proposal — not finalized)`,
      amount,
      dueDate: due ?? null,
      estimated: true,
      quotePlaceholder: true,
      quoteKind: row.kind,
      structureNames: structure ? [structure] : undefined,
      anchor: due
        ? `Proposal node pricing — mid-task ${fmtAnchorDate(due)}`
        : 'Proposal node pricing — quote not approved',
      projectName: apProject.projectName || row.projectLabel,
      projectId: apProject.projectId || row.projectId,
      dealId: row.dealId,
      taskId: row.taskId,
    });
  }

  for (const row of inputs.awardedQuoteAp ?? []) {
    const amount = Math.round(row.amount);
    if (amount <= 0) continue;
    const key = firstMonthKey;
    apByMonth.set(key, (apByMonth.get(key) ?? 0) + amount);
    const apProject = resolveProject({
      projectId: row.projectId,
      dealId: row.dealId,
    });
    const names = (row.structureNames ?? []).map((name) => name.trim()).filter(Boolean);
    pushItem(key, {
      kind: 'ap',
      projectLabel: row.vendorName,
      label: row.label,
      amount,
      dueDate: null,
      estimated: true,
      structureNames: names.length ? names : undefined,
      anchor: 'Approved quote — not yet on a supplier bill',
      projectName: apProject.projectName,
      projectId: apProject.projectId || row.projectId,
      dealId: row.dealId,
      submissionId: row.submissionId,
    });
  }

  for (const row of inputs.contractorInvoiceAp ?? []) {
    const amount = Math.round(row.amount);
    if (amount <= 0) continue;
    const due = row.dueDate;
    let key: string;
    if (!due) {
      key = firstMonthKey;
    } else {
      const dueKey = toMonthKey(startOfMonth(due));
      key = dueKey < firstMonthKey ? firstMonthKey : dueKey;
    }
    if (key > lastMonthKey) key = lastMonthKey;
    apByMonth.set(key, (apByMonth.get(key) ?? 0) + amount);
    const apProject = resolveProject({
      projectId: row.projectId,
      dealId: row.dealId,
    });
    pushItem(key, {
      kind: 'ap',
      projectLabel: row.vendorName,
      label: row.label,
      amount,
      dueDate: due ?? null,
      estimated: false,
      anchor: 'Contractor invoice — install',
      projectName: apProject.projectName,
      projectId: apProject.projectId || row.projectId,
      dealId: row.dealId,
      contractorInvoiceId: row.invoiceId,
    });
  }

  const budgetPoRows: Array<{ monthKey: string; amount: number; group: string }> = [];
  for (const row of inputs.issuedPurchaseAp ?? []) {
    const amount = Math.round(row.amount);
    if (amount <= 0) continue;
    const due = row.dueDate;
    let key: string;
    if (!due) {
      key = firstMonthKey;
    } else {
      const dueKey = toMonthKey(startOfMonth(due));
      key = dueKey < firstMonthKey ? firstMonthKey : dueKey;
    }
    if (key > lastMonthKey) key = lastMonthKey;
    const budgetGroup = opexBudgetGroupForIssuedPurchase(row, inputs.opexBudgetMonthlyByGroup);
    if (budgetGroup) {
      budgetPoRows.push({ monthKey: key, amount, group: budgetGroup });
    } else {
      apByMonth.set(key, (apByMonth.get(key) ?? 0) + amount);
    }
    const apProject = resolveProject({
      projectId: row.projectId,
      dealId: row.dealId,
    });
    const po = (row.poNumber || '').trim();
    pushItem(key, {
      kind: 'ap',
      projectLabel: row.vendorName,
      label: po ? `${po} · ${row.title}` : row.title,
      amount,
      dueDate: due ?? null,
      estimated: false,
      spendClass: row.spendClass,
      withinBurnBudget: Boolean(budgetGroup),
      overheadGroup: budgetGroup || row.overheadGroup,
      anchor: budgetGroup
        ? 'Inside the monthly budget'
        : 'Issued PO — not marked purchased',
      projectName: apProject.projectName,
      projectId: apProject.projectId,
      dealId: row.dealId,
      requisitionId: row.requisitionId,
      previewStoragePath: row.previewStoragePath,
      previewFileName: row.previewFileName,
      previewFileUrl: row.previewFileUrl,
    });
  }

  const budgetBurnExcess = opexBudgetBurnExcessByMonth(
    budgetPoRows,
    inputs.opexBudgetMonthlyByGroup ?? {},
  );

  const burnByMonth = new Map<string, number>();
  const payrollBurnByMonth = new Map<string, number>();
  for (const bucket of inputs.burnBuckets) {
    const key = toMonthKey(bucket.month);
    burnByMonth.set(key, Math.round(bucket.total));
    payrollBurnByMonth.set(key, Math.round(bucket.payrollTotal));
  }

  const plannedByMonth = new Map<string, number>();
  for (const row of inputs.plannedItems ?? []) {
    const amount = Math.round(row.amount);
    if (amount <= 0) continue;
    const due = row.dueDate;
    if (!due) continue;
    const dueKey = toMonthKey(startOfMonth(due));
    if (dueKey < firstMonthKey) continue;
    let key = dueKey;
    if (key > lastMonthKey) key = lastMonthKey;
    plannedByMonth.set(key, (plannedByMonth.get(key) ?? 0) + amount);
    pushItem(key, {
      kind: 'planned',
      plannedKind: row.plannedKind,
      projectLabel: row.projectLabel,
      label: row.label,
      amount,
      dueDate: due,
      estimated: row.estimated,
      anchor: row.plannedKind === 'bonus'
        ? 'Planned bonus payout'
        : row.plannedKind === 'tax'
          ? 'Planned tax payment'
          : 'Planned cash out',
    });
  }

  const jobByMonth = new Map<string, number>();
  for (const row of inputs.jobCostItems ?? []) {
    const amount = Math.round(row.amount);
    if (amount <= 0) continue;
    const due = row.dueDate;
    let key: string;
    if (!due) {
      key = firstMonthKey;
    } else {
      const dueKey = toMonthKey(startOfMonth(due));
      if (dueKey < firstMonthKey) continue;
      key = dueKey;
    }
    if (key > lastMonthKey) key = lastMonthKey;
    jobByMonth.set(key, (jobByMonth.get(key) ?? 0) + amount);
    const jobProject = resolveProject({
      projectId: row.projectId,
      dealId: row.dealId,
      taskId: row.taskId,
    });
    pushItem(key, {
      kind: 'job',
      jobBucket: row.bucket,
      projectLabel: row.projectLabel,
      label: jobCostItemLabel(row),
      amount,
      dueDate: due,
      estimated: true,
      anchor: row.anchor,
      projectName: jobProject.projectName || row.projectName || row.projectLabel,
      projectId: jobProject.projectId || row.projectId,
      taskId: row.taskId,
    });
  }

  const months: CashFlowProjectionMonth[] = [];
  let balance = Math.round(inputs.startingBalance);
  let lowestBalance: number | null = null;
  let lowestBalanceMonthKey: string | null = null;
  let runwayMonths: number | null = null;
  const totals = { arIn: 0, apOut: 0, burn: 0, plannedOut: 0, jobOut: 0 };

  const payrollSchedule = inputs.payrollSchedule
    ? (parsePayrollSchedule(inputs.payrollSchedule) ?? inputs.payrollSchedule)
    : null;

  for (let i = 0; i < monthsAhead; i++) {
    const month = addMonths(firstMonth, i);
    const monthKey = toMonthKey(month);
    const arIn = Math.round(arByMonth.get(monthKey) ?? 0);
    const apOut = Math.round(apByMonth.get(monthKey) ?? 0);
    const averagePayroll = Math.round(payrollBurnByMonth.get(monthKey) ?? 0);
    const averageBurn = Math.round((burnByMonth.get(monthKey) ?? 0) + (budgetBurnExcess.get(monthKey) ?? 0));
    const averageOverhead = Math.max(0, averageBurn - averagePayroll);
    let payrollBurn = averagePayroll;
    if (payrollSchedule && averagePayroll > 0) {
      const monthStart = new Date(month.getFullYear(), month.getMonth(), 1);
      const monthEnd = new Date(month.getFullYear(), month.getMonth() + 1, 0);
      const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const isCurrentMonth = monthStart.getTime() === new Date(todayStart.getFullYear(), todayStart.getMonth(), 1).getTime();
      const payDates = listPayDatesInRange(payrollSchedule, monthStart, monthEnd);
      let paydayCount = isCurrentMonth
        ? payDates.filter((ymd) => {
          const [y, m, d] = ymd.split('-').map(Number);
          return new Date(y, m - 1, d) >= todayStart;
        }).length
        : payDates.length;
      // Match daily chart: if no payday remains this month, still charge one check.
      if (paydayCount === 0 && (isCurrentMonth || payDates.length === 0)) paydayCount = 1;
      if (paydayCount === 0) paydayCount = payDates.length;
      payrollBurn = scaleMonthlyPayrollForPaydays(averagePayroll, paydayCount, payrollSchedule);
    }
    const burn = averageOverhead + payrollBurn;
    const plannedOut = Math.round(plannedByMonth.get(monthKey) ?? 0);
    const jobOut = Math.round(jobByMonth.get(monthKey) ?? 0);
    const net = arIn - apOut - burn - plannedOut - jobOut;
    balance += net;

    totals.arIn += arIn;
    totals.apOut += apOut;
    totals.burn += burn;
    totals.plannedOut += plannedOut;
    totals.jobOut += jobOut;

    if (lowestBalance === null || balance < lowestBalance) {
      lowestBalance = balance;
      lowestBalanceMonthKey = monthKey;
    }
    if (runwayMonths === null && balance < 0) {
      runwayMonths = i;
    }

    // AR, then AP, then leftover job costs, then planned — biggest first within each kind.
    const kindRank = { ar: 0, ap: 1, job: 2, planned: 3 };
    const items = (itemsByMonth.get(monthKey) ?? []).sort(
      (a, b) => (a.kind === b.kind ? b.amount - a.amount : kindRank[a.kind] - kindRank[b.kind]),
    );

    months.push({
      monthKey,
      month,
      arIn,
      apOut,
      burn,
      payrollBurn,
      plannedOut,
      jobOut,
      net,
      endingBalance: balance,
      hasEstimatedAr: arEstimatedMonths.has(monthKey),
      items,
    });
  }

  return {
    months,
    startingBalance: Math.round(inputs.startingBalance),
    lowestBalance,
    lowestBalanceMonthKey,
    runwayMonths,
    arBeyondWindow: Math.round(arBeyondWindow),
    totals,
  };
}
